import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Link,
  useTheme,
  alpha,
  IconButton,
  Button,
  Skeleton,
} from "@mui/material";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LaunchIcon from "@mui/icons-material/Launch";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import CardMedia from "@mui/material/CardMedia";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faInstagram,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";

interface Article {
  title: string;
  source: string;
  date: string;
  url: string;
  image?: string;
}

interface SocialMedia {
  platform: string;
  icon: React.ReactNode;
  url: string;
  followers: string;
  color: string;
}

const awards = [
  {
    title: "Asian Telecom Awards – Mobile Operator of the Year",
    year: "2023",
    image: "AsianTelAwards.png",
  },
  {
    title: "WSIS Prizes (World Summit on the Information Society)",
    year: "2025",
    image: "WISIS_award.png",
  },
  {
    title: "The Stevie Awards for Technology Excellence",
    year: "2024",
    image: "stevie_award.png",
  },
];

const mediaArticles = [
  {
    title: "Lao Telecom celebrates 29 years of success",
    source: "Vientiane Times",
    date: "Nov 17, 2025",
    url: "https://www.vientianetimes.org.la/freefreenews/freecontent_222_LaoTelecom_y25.php",
  },
  {
    title: "Lao Telecom Launches 5G Network in Laos",
    source: "Laotian Times",
    date: "Sep 02, 2020",
    url: "https://www.vientianetimes.org.la/freefreenews/freecontent_222_LaoTelecom_y25.php",
  },
  {
    title: "Lao Telecom, Mono and JAS to bring EPL, FA Cup football to Laos",
    source: "Developing Telecoms",
    date: "Jun 24, 2025",
    url: "https://developingtelecoms.com/telecom-technology/60-consumer-ecosystems/18672-lao-telecom-mono-and-jas-to-bring-epl-fa-cup-football-to-laos.html",
  },
];

const socialMediaPlatforms: SocialMedia[] = [
  {
    platform: "Facebook",
    icon: <FontAwesomeIcon icon={faFacebookF} />,
    url: "https://www.facebook.com/Laotel/",
    followers: "Follow us on Facebook",
    color: "#1877F2",
  },
  {
    platform: "Instagram",
    icon: <FontAwesomeIcon icon={faInstagram} />,
    url: "https://www.instagram.com/lao.telecom/?hl=en",
    followers: "Follow us on Instagram",
    color: "#E4405F",
  },
  {
    platform: "TikTok",
    icon: <FontAwesomeIcon icon={faTiktok} />,
    url: "https://www.tiktok.com/@laotel",
    followers: "Follow us on TikTok",
    color: "#000000",
  },
];

const SectionCard = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.04)}`,
      }}
    >
      <CardContent>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            {icon}
          </Box>

          <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.9rem",
              lineHeight: 1.65,
              color: theme.palette.text.secondary,
            }}
          >
            {children}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const MediaCoverageCard = ({ article }: { article: Article }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      component={Link}
      href={article.url}
      underline="none"
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        height: "80%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
        boxShadow: isHovered
          ? `0 12px 24px ${alpha(theme.palette.common.black, 0.15)}`
          : `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      {/* Image Section with Icon */}
      <Box
        sx={{
          position: "relative",
          height: 200,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            transform: isHovered
              ? "scale(1.1) rotate(5deg)"
              : "scale(1) rotate(0deg)",
            transition: "transform 0.4s ease-in-out",
          }}
        >
          <LaunchIcon
            sx={{
              fontSize: 80,
              color: alpha(theme.palette.primary.main, 0.3),
            }}
          />
        </Box>

        {/* Overlay on hover */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: alpha(theme.palette.common.black, isHovered ? 0.2 : 0),
            transition: "background-color 0.3s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isHovered && (
            <OpenInNewIcon
              sx={{
                color: "#fff",
                fontSize: 32,
                animation: "fadeIn 0.2s ease-in",
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "scale(0.8)" },
                  to: { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
          )}
        </Box>

        {/* Source chip */}
        <Chip
          label={article.source}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: "blur(8px)",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      </Box>

      {/* Content Section */}
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Stack spacing={2} height="100%">
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: 1.4,
              color: theme.palette.text.primary,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.title}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: "auto" }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.8rem",
              }}
            >
              {article.date}
            </Typography>
            <ArrowForwardIcon
              fontSize="small"
              sx={{
                color: theme.palette.primary.main,
                transform: isHovered ? "translateX(4px)" : "translateX(0)",
                transition: "transform 0.3s",
              }}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const SocialMediaCompact = ({ social }: { social: SocialMedia }) => {
  const theme = useTheme();

  return (
    <Card
      component={Link}
      href={social.url}
      underline="none"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.5,
        height: 64,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        boxShadow: "none",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: alpha(social.color, 0.4),
          bgcolor: alpha(social.color, 0.04),
        },
      }}
    >
      {/* Icon */}
      <Box sx={{ color: social.color, display: "flex" }}>
        <Box sx={{ fontSize: 26 }}>
          {React.cloneElement(social.icon as React.ReactElement)}
        </Box>
      </Box>

      {/* Text */}
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
          {social.platform}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.8rem",
            color: theme.palette.text.secondary,
          }}
        >
          {social.followers}
        </Typography>
      </Box>

      {/* Arrow */}
      <ArrowForwardIcon
        sx={{
          fontSize: 18,
          color: theme.palette.text.disabled,
        }}
      />
    </Card>
  );
};

const CompanyProfilePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [hoveredAward, setHoveredAward] = useState<number | null>(null);

  return (
    <Box py={8}>
      <Container maxWidth="lg">
        <Stack spacing={8}>
          {/* HERO */}
          <Box
            sx={{
              position: "relative",
              height: { xs: 300, md: 420 },
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <CardMedia
              component="img"
              image="laotel_hq.png"
              alt="Lao Telecom Headquarters"
              sx={{ height: "100%", objectFit: "cover" }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.65))",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Container maxWidth="md">
                <Stack spacing={2}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: { xs: "2rem", md: "2.6rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    Our Commitment to Service Excellence
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: "1rem",
                      maxWidth: 600,
                    }}
                  >
                    Delivering reliable, human-centered digital connectivity for
                    Laos — every day, everywhere.
                  </Typography>
                </Stack>
              </Container>
            </Box>
          </Box>
          {/* CORE PILLARS */}
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="stretch"
            gap={3}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                width: { xs: "100%", md: "33.333%" },
              }}
            >
              <SectionCard
                icon={<NetworkCheckIcon sx={{ fontSize: 30 }} />}
                title="Nationwide Reliability"
              >
                Extensive nationwide coverage with real-time network monitoring
                to ensure stability across Laos.
              </SectionCard>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                width: { xs: "100%", md: "33.333%" },
              }}
            >
              <SectionCard
                icon={<RocketLaunchIcon sx={{ fontSize: 30 }} />}
                title="Innovation with Purpose"
              >
                First to launch 3G, 4G, and 5G — empowering people and
                businesses with meaningful technology.
              </SectionCard>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                width: { xs: "100%", md: "33.333%" },
              }}
            >
              <SectionCard
                icon={<SupportAgentIcon sx={{ fontSize: 30 }} />}
                title="Customer-Centered Care"
              >
                24/7 multi-channel support backed by service centers in every
                province.
              </SectionCard>
            </Box>
          </Box>

          {/* AWARDS */}
          <Box textAlign="center">
            <Typography sx={{ fontWeight: 600, fontSize: "1.6rem", mb: 3 }}>
              Recognition & Awards
            </Typography>

            <Grid container spacing={3}>
              {awards.map((award, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    onMouseEnter={() => setHoveredAward(index)}
                    onMouseLeave={() => setHoveredAward(null)}
                    sx={{
                      transform:
                        hoveredAward === index ? "translateY(-6px)" : "none",
                      transition: "all 0.3s",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={award.image}
                      sx={{ height: 160, objectFit: "contain" }}
                    />
                    <CardContent>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                        {award.title}
                      </Typography>
                      <Chip label={award.year} size="small" sx={{ mt: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Button
              variant="outlined"
              endIcon={<LaunchIcon />}
              sx={{ mt: 4 }}
              onClick={() =>
                navigate("https://www.laotel.com/la/vision-mission.php?Lang=en")
              }
            >
              See More Awards
            </Button>
          </Box>
          {/* MEDIA COVERAGE */}
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1.6rem",
                mb: 1,
                textAlign: "center",
              }}
            >
              Media Coverage
            </Typography>
            <Typography
              sx={{
                fontSize: "0.95rem",
                color: theme.palette.text.secondary,
                textAlign: "center",
                mb: 4,
              }}
            >
              Latest news and stories about Lao Telecom
            </Typography>

            <Grid container spacing={3}>
              {mediaArticles.map((article, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <MediaCoverageCard article={article} />
                </Grid>
              ))}
            </Grid>
          </Box>
          {/* SOCIAL MEDIA */}
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1.3rem",
                mb: 2,
                textAlign: "center",
              }}
            >
              Connect With Us
            </Typography>

            <Grid container spacing={2} justifyContent="center">
              {socialMediaPlatforms.map((social, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <SocialMediaCompact social={social} />
                </Grid>
              ))}
            </Grid>
          </Box>
          {/* QUOTE */}
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              textAlign: "center",
            }}
          >
            <FormatQuoteIcon
              sx={{ fontSize: 48, color: theme.palette.primary.main }}
            />
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontStyle: "italic",
                maxWidth: 900,
                mx: "auto",
              }}
            >
              Excellence is not a one-time achievement — it's a daily
              commitment.
            </Typography>
          </Box>
          {/* LOCATION */}
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1.6rem",
                mb: 3,
                textAlign: "center",
              }}
            >
              Our Location
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontWeight: 600 }}>
                  Lao Telecom Headquarters
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", mt: 1 }}>
                  SayLom Village, Chanthabouly District
                  <br />
                  Vientiane Capital, Lao PDR
                </Typography>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    height: 280,
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3795.1933152882675!2d102.60994177510862!3d17.96974088302015!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3124687cf331af9b%3A0x8113812c081d25e3!2sXJ96%2BXX%20Lao%20Telecom%20Head%20Office%2C%20Saylom%20Rd%2C%20Vientiane!5e0!3m2!1sen!2sla!4v1769677485383!5m2!1sen!2sla"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Lao Telecom Headquarters Map"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default CompanyProfilePage;
